import { useEffect, useMemo, useState } from "react";
import { Navigate, useParams } from "react-router-dom";
import { Loader2, Printer, Download } from "lucide-react";
import { useAuth } from "../lib/auth";
import { api, apiError } from "../lib/api";
import { downloadCSV } from "../lib/csv";

const short = (s) => (s ?? "").split(" · ")[0];

const Header = ({ d }) => (
  <div className="flex flex-wrap items-start justify-between gap-6 border-b-2 border-black pb-4">
    <div>
      <img src="/brand/logo-green.png" alt="Santiagueño" className="h-12 w-auto" />
      <h1 data-testid="manifest-title" className="mt-3 font-display text-3xl">Lista de pasajeros</h1>
      <p className="text-sm">{d.route?.name}</p>
    </div>
    <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-sm">
      <dt className="font-semibold">Fecha</dt><dd data-testid="manifest-date">{d.trip.date} · {d.trip.departure} hs</dd>
      <dt className="font-semibold">Unidad</dt><dd>{d.unit ? `Interno ${d.unit.interno} · ${d.unit.patente}` : "Sin asignar"}</dd>
      <dt className="font-semibold">Conductores</dt><dd data-testid="manifest-drivers">{d.drivers?.length ? d.drivers.map((x) => `${x.nombre}${x.licencia ? ` (Lic. ${x.licencia})` : ""}`).join(" · ") : "Sin asignar"}</dd>
    </dl>
  </div>
);

const Boarding = ({ rows, stops }) => {
  const by = stops.map((s, i) => ({ s, up: rows.filter((r) => r.from_idx === i).length, down: rows.filter((r) => r.to_idx === i).length })).filter((x) => x.up || x.down);
  return (
    <div className="mt-6 break-inside-avoid">
      <h2 className="font-display-up text-lg">Resumen por parada</h2>
      <table data-testid="manifest-boarding" className="mt-2 w-full border-collapse text-sm">
        <thead><tr className="border-b border-black text-left"><th className="py-1.5">Parada</th><th>Hora</th><th>Suben</th><th>Bajan</th></tr></thead>
        <tbody>{by.map(({ s, up, down }) => <tr key={s.id} className="border-b border-black/15"><td className="py-1.5">{s.name}</td><td className="font-mono">{s.time}</td><td>{up || "—"}</td><td>{down || "—"}</td></tr>)}</tbody>
      </table>
    </div>
  );
};

export default function AdminManifiesto() {
  const { id } = useParams();
  const { user } = useAuth();
  const [d, setD] = useState(null);
  const [err, setErr] = useState("");
  const [sort, setSort] = useState("seat");
  useEffect(() => { if (user) api.get(`/admin/trips/${id}`).then((r) => setD(r.data)).catch((e) => setErr(apiError(e))); }, [user, id]);

  const stops = useMemo(() => {
    if (!d?.route) return [];
    const [h, m] = d.trip.departure.split(":").map(Number);
    return d.route.stops.map((s) => { const t = h * 60 + m + s.offset_min; return { ...s, time: `${String(Math.floor(t / 60) % 24).padStart(2, "0")}:${String(t % 60).padStart(2, "0")}` }; });
  }, [d]);
  const rows = useMemo(() => {
    const list = (d?.bookings ?? []).filter((b) => b.status !== "bloqueado").flatMap((b) => b.seats.map((seat) => ({ ...b, seat })));
    return list.sort((a, b) => (sort === "stop" ? a.from_idx - b.from_idx || a.seat - b.seat : a.seat - b.seat));
  }, [d, sort]);

  if (user === null) return <div className="grid min-h-[60vh] place-items-center"><Loader2 className="h-6 w-6 animate-spin" /></div>;
  if (user === false) return <Navigate to="/admin" replace />;
  if (err) return <p data-testid="manifest-error" className="p-10 text-red-700">{err}</p>;
  if (!d) return <div className="grid min-h-[60vh] place-items-center"><Loader2 className="h-6 w-6 animate-spin" /></div>;

  const csv = () => downloadCSV(`pasajeros_${d.trip.date}_${d.trip.departure.replace(":", "")}.csv`, [["Butaca", "Pasajero", "DNI", "Sube en", "Hora", "Baja en", "Código", "Estado"],
    ...rows.map((r) => [r.seat, r.pax?.[String(r.seat)]?.name || r.name, r.pax?.[String(r.seat)]?.dni || r.dni, stops[r.from_idx]?.name, stops[r.from_idx]?.time, stops[r.to_idx]?.name, r.code, r.status])]);

  return (
    <section data-testid="manifest-page" className="mx-auto max-w-[1000px] bg-white px-5 py-10 text-black md:px-10 print:max-w-none print:p-0">
      <div className="mb-6 flex flex-wrap items-center gap-2 print:hidden">
        <button onClick={() => window.print()} data-testid="manifest-print" className="inline-flex items-center gap-2 rounded-full bg-[var(--forest)] px-5 py-2.5 text-sm font-bold text-white"><Printer className="h-4 w-4" /> Imprimir</button>
        <button onClick={csv} data-testid="manifest-csv" className="inline-flex items-center gap-2 rounded-full bg-[var(--ink)] px-5 py-2.5 text-sm font-bold text-white"><Download className="h-4 w-4" /> Descargar Excel (CSV)</button>
        <select data-testid="manifest-sort" value={sort} onChange={(e) => setSort(e.target.value)} className="h-10 rounded-full border border-black/15 px-4 text-sm">
          <option value="seat">Ordenar por butaca</option>
          <option value="stop">Ordenar por parada de subida</option>
        </select>
      </div>
      <Header d={d} />
      <p data-testid="manifest-count" className="mt-4 text-sm font-semibold">{rows.length} pasajeros</p>
      <div className="overflow-x-auto">
        <table data-testid="manifest-table" className="mt-2 w-full min-w-[640px] border-collapse text-sm">
          <thead><tr className="border-b-2 border-black text-left"><th className="py-2 pr-2">Butaca</th><th>Pasajero</th><th>DNI</th><th>Sube en</th><th>Baja en</th><th>Código</th><th className="w-16">Subió</th></tr></thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan={7} className="py-6 text-center">Sin pasajeros todavía.</td></tr>}
            {rows.map((r) => (
              <tr key={`${r.code}-${r.seat}`} className="break-inside-avoid border-b border-black/15">
                <td className="py-2 pr-2 font-mono text-base font-bold">{r.seat}</td>
                <td>{r.pax?.[String(r.seat)]?.name || r.companions?.[String(r.seat)]?.name || r.name}{r.status === "reservado" && <span className="ml-1 text-xs">(reserva)</span>}</td>
                <td className="font-mono">{r.pax?.[String(r.seat)]?.dni || r.dni}</td>
                <td>{short(stops[r.from_idx]?.name)} <span className="font-mono text-xs">{stops[r.from_idx]?.time}</span></td>
                <td>{short(stops[r.to_idx]?.name)}</td>
                <td className="font-mono text-xs">{r.code}</td>
                <td>{r.boarded_at || r.boarded_seats?.includes(r.seat) ? <span className="inline-grid h-5 w-5 place-items-center border border-black text-xs font-bold">✓</span> : <span className="inline-block h-5 w-5 border border-black" />}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <Boarding rows={rows} stops={stops} />
      <div className="mt-12 grid grid-cols-2 gap-10 text-center text-xs">
        <p className="border-t border-black pt-2">Firma conductor</p>
        <p className="border-t border-black pt-2">Firma responsable boletería</p>
      </div>
    </section>
  );
}
